import type { Allergen, DietaryTag } from "@dishfolio/contracts";

export * from "./negotiate.ts";

/**
 * Static strings shown to diners on public menus. Dashboard UI localization is separate
 * (the dashboard ships in English first; see apps/dashboard/src/i18n).
 */
export type DinerStringKey =
  | "search"
  | "noResults"
  | "allergens"
  | "contains"
  | "add"
  | "addToOrder"
  | "viewOrder"
  | "yourOrder"
  | "table"
  | "notes"
  | "notesPlaceholder"
  | "submitOrder"
  | "subtotal"
  | "tax"
  | "taxIncluded"
  | "total"
  | "required"
  | "chooseUpTo"
  | "chooseAtLeast"
  | "unavailable"
  | "orderPlaced"
  | "status.placed"
  | "status.accepted"
  | "status.preparing"
  | "status.ready"
  | "status.served"
  | "status.rejected"
  | "status.cancelled"
  | "payAtTable"
  | "fallbackNotice"
  | "language"
  | "emptyCart"
  | "remove"
  | "orderingPaused"
  | "close"
  | "trackOrder"
  | "priceChanged"
  | "menuUnavailable";

type Dict = Record<DinerStringKey, string>;

const en: Dict = {
  search: "Search",
  noResults: "No dishes match your search.",
  allergens: "Allergens",
  contains: "Contains",
  add: "Add",
  addToOrder: "Add to order",
  viewOrder: "View order",
  yourOrder: "Your order",
  table: "Table",
  notes: "Notes",
  notesPlaceholder: "Allergies, preferences…",
  submitOrder: "Place order",
  subtotal: "Subtotal",
  tax: "Tax",
  taxIncluded: "incl. tax",
  total: "Total",
  required: "Required",
  chooseUpTo: "Choose up to {n}",
  chooseAtLeast: "Choose at least {n}",
  unavailable: "Unavailable",
  orderPlaced: "Order sent!",
  "status.placed": "Sent to the restaurant",
  "status.accepted": "Accepted",
  "status.preparing": "Being prepared",
  "status.ready": "Ready",
  "status.served": "Served",
  "status.rejected": "Declined by the restaurant",
  "status.cancelled": "Cancelled",
  payAtTable: "Pay at the table or counter.",
  fallbackNotice: "Some items are shown in the original language.",
  language: "Language",
  emptyCart: "Your order is empty.",
  remove: "Remove",
  orderingPaused: "Ordering is paused right now. Please ask our staff.",
  close: "Close",
  trackOrder: "Track order",
  priceChanged: "Prices were updated. Please review your order.",
  menuUnavailable: "This menu is not available.",
};

const de: Dict = {
  search: "Suchen",
  noResults: "Keine Gerichte gefunden.",
  allergens: "Allergene",
  contains: "Enthält",
  add: "Hinzufügen",
  addToOrder: "Zur Bestellung",
  viewOrder: "Bestellung ansehen",
  yourOrder: "Ihre Bestellung",
  table: "Tisch",
  notes: "Anmerkungen",
  notesPlaceholder: "Allergien, Wünsche…",
  submitOrder: "Bestellen",
  subtotal: "Zwischensumme",
  tax: "MwSt.",
  taxIncluded: "inkl. MwSt.",
  total: "Gesamt",
  required: "Pflicht",
  chooseUpTo: "Bis zu {n} wählen",
  chooseAtLeast: "Mindestens {n} wählen",
  unavailable: "Nicht verfügbar",
  orderPlaced: "Bestellung gesendet!",
  "status.placed": "An das Restaurant gesendet",
  "status.accepted": "Angenommen",
  "status.preparing": "In Zubereitung",
  "status.ready": "Fertig",
  "status.served": "Serviert",
  "status.rejected": "Vom Restaurant abgelehnt",
  "status.cancelled": "Storniert",
  payAtTable: "Bezahlung am Tisch oder an der Theke.",
  fallbackNotice: "Einige Einträge werden in der Originalsprache angezeigt.",
  language: "Sprache",
  emptyCart: "Ihre Bestellung ist leer.",
  remove: "Entfernen",
  orderingPaused: "Bestellungen sind gerade pausiert. Bitte fragen Sie unser Personal.",
  close: "Schließen",
  trackOrder: "Bestellung verfolgen",
  priceChanged: "Die Preise wurden aktualisiert. Bitte prüfen Sie Ihre Bestellung.",
  menuUnavailable: "Diese Speisekarte ist nicht verfügbar.",
};

const bs: Dict = {
  search: "Pretraga",
  noResults: "Nema jela koja odgovaraju pretrazi.",
  allergens: "Alergeni",
  contains: "Sadrži",
  add: "Dodaj",
  addToOrder: "Dodaj u narudžbu",
  viewOrder: "Pogledaj narudžbu",
  yourOrder: "Vaša narudžba",
  table: "Sto",
  notes: "Napomena",
  notesPlaceholder: "Alergije, želje…",
  submitOrder: "Naruči",
  subtotal: "Međuzbir",
  tax: "PDV",
  taxIncluded: "uklj. PDV",
  total: "Ukupno",
  required: "Obavezno",
  chooseUpTo: "Izaberite do {n}",
  chooseAtLeast: "Izaberite najmanje {n}",
  unavailable: "Nije dostupno",
  orderPlaced: "Narudžba poslana!",
  "status.placed": "Poslano restoranu",
  "status.accepted": "Prihvaćeno",
  "status.preparing": "U pripremi",
  "status.ready": "Spremno",
  "status.served": "Posluženo",
  "status.rejected": "Restoran je odbio narudžbu",
  "status.cancelled": "Otkazano",
  payAtTable: "Plaćanje za stolom ili na šanku.",
  fallbackNotice: "Neke stavke su prikazane na izvornom jeziku.",
  language: "Jezik",
  emptyCart: "Vaša narudžba je prazna.",
  remove: "Ukloni",
  orderingPaused: "Narudžbe su trenutno pauzirane. Obratite se osoblju.",
  close: "Zatvori",
  trackOrder: "Prati narudžbu",
  priceChanged: "Cijene su ažurirane. Provjerite narudžbu.",
  menuUnavailable: "Ovaj meni nije dostupan.",
};

const hr: Dict = { ...bs, table: "Stol", notes: "Napomena", subtotal: "Međuzbroj", "status.rejected": "Restoran je odbio narudžbu", menuUnavailable: "Ovaj jelovnik nije dostupan." };
const srLatn: Dict = { ...bs, table: "Sto", allergens: "Alergeni", yourOrder: "Vaša porudžbina", viewOrder: "Pogledaj porudžbinu", addToOrder: "Dodaj u porudžbinu", submitOrder: "Poruči", orderPlaced: "Porudžbina poslata!", emptyCart: "Vaša porudžbina je prazna.", trackOrder: "Prati porudžbinu" };

const fr: Dict = {
  ...en,
  search: "Rechercher",
  noResults: "Aucun plat ne correspond.",
  allergens: "Allergènes",
  contains: "Contient",
  add: "Ajouter",
  addToOrder: "Ajouter à la commande",
  viewOrder: "Voir la commande",
  yourOrder: "Votre commande",
  table: "Table",
  notes: "Remarques",
  submitOrder: "Commander",
  subtotal: "Sous-total",
  tax: "TVA",
  taxIncluded: "TVA incluse",
  total: "Total",
  required: "Obligatoire",
  unavailable: "Indisponible",
  orderPlaced: "Commande envoyée !",
  "status.placed": "Envoyée au restaurant",
  "status.accepted": "Acceptée",
  "status.preparing": "En préparation",
  "status.ready": "Prête",
  "status.served": "Servie",
  "status.rejected": "Refusée par le restaurant",
  "status.cancelled": "Annulée",
  payAtTable: "Paiement à table ou au comptoir.",
  fallbackNotice: "Certains plats sont affichés dans la langue d'origine.",
  language: "Langue",
  close: "Fermer",
};

const it: Dict = {
  ...en,
  search: "Cerca",
  noResults: "Nessun piatto trovato.",
  allergens: "Allergeni",
  contains: "Contiene",
  add: "Aggiungi",
  addToOrder: "Aggiungi all'ordine",
  viewOrder: "Vedi ordine",
  yourOrder: "Il tuo ordine",
  table: "Tavolo",
  notes: "Note",
  submitOrder: "Ordina",
  subtotal: "Subtotale",
  tax: "IVA",
  taxIncluded: "IVA inclusa",
  total: "Totale",
  required: "Obbligatorio",
  unavailable: "Non disponibile",
  orderPlaced: "Ordine inviato!",
  "status.placed": "Inviato al ristorante",
  "status.accepted": "Accettato",
  "status.preparing": "In preparazione",
  "status.ready": "Pronto",
  "status.served": "Servito",
  "status.rejected": "Rifiutato dal ristorante",
  "status.cancelled": "Annullato",
  payAtTable: "Paga al tavolo o alla cassa.",
  fallbackNotice: "Alcuni piatti sono mostrati nella lingua originale.",
  language: "Lingua",
  close: "Chiudi",
};

const es: Dict = {
  ...en,
  search: "Buscar",
  noResults: "Ningún plato coincide.",
  allergens: "Alérgenos",
  contains: "Contiene",
  add: "Añadir",
  addToOrder: "Añadir al pedido",
  viewOrder: "Ver pedido",
  yourOrder: "Tu pedido",
  table: "Mesa",
  notes: "Notas",
  submitOrder: "Pedir",
  subtotal: "Subtotal",
  tax: "IVA",
  taxIncluded: "IVA incluido",
  total: "Total",
  required: "Obligatorio",
  unavailable: "No disponible",
  orderPlaced: "¡Pedido enviado!",
  "status.placed": "Enviado al restaurante",
  "status.accepted": "Aceptado",
  "status.preparing": "En preparación",
  "status.ready": "Listo",
  "status.served": "Servido",
  "status.rejected": "Rechazado por el restaurante",
  "status.cancelled": "Cancelado",
  payAtTable: "Paga en la mesa o en la barra.",
  fallbackNotice: "Algunos platos se muestran en el idioma original.",
  language: "Idioma",
  close: "Cerrar",
};

const DICTS: Record<string, Dict> = { en, de, bs, hr, "sr-Latn": srLatn, sr: srLatn, fr, it, es };

export function dinerT(lang: string, key: DinerStringKey, vars?: Record<string, string | number>): string {
  const dict = DICTS[lang] ?? DICTS[lang.split("-")[0]!] ?? en;
  let s = dict[key] ?? en[key];
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}

const ALLERGEN_LABELS: Record<string, Record<Allergen, string>> = {
  en: {
    gluten: "Gluten", crustaceans: "Crustaceans", eggs: "Eggs", fish: "Fish", peanuts: "Peanuts", soybeans: "Soy",
    milk: "Milk", nuts: "Tree nuts", celery: "Celery", mustard: "Mustard", sesame: "Sesame", sulphites: "Sulphites",
    lupin: "Lupin", molluscs: "Molluscs",
  },
  de: {
    gluten: "Gluten", crustaceans: "Krebstiere", eggs: "Eier", fish: "Fisch", peanuts: "Erdnüsse", soybeans: "Soja",
    milk: "Milch", nuts: "Schalenfrüchte", celery: "Sellerie", mustard: "Senf", sesame: "Sesam", sulphites: "Sulfite",
    lupin: "Lupinen", molluscs: "Weichtiere",
  },
  bs: {
    gluten: "Gluten", crustaceans: "Rakovi", eggs: "Jaja", fish: "Riba", peanuts: "Kikiriki", soybeans: "Soja",
    milk: "Mlijeko", nuts: "Orašasti plodovi", celery: "Celer", mustard: "Senf", sesame: "Susam", sulphites: "Sulfiti",
    lupin: "Lupina", molluscs: "Mekušci",
  },
  fr: {
    gluten: "Gluten", crustaceans: "Crustacés", eggs: "Œufs", fish: "Poisson", peanuts: "Arachides", soybeans: "Soja",
    milk: "Lait", nuts: "Fruits à coque", celery: "Céleri", mustard: "Moutarde", sesame: "Sésame", sulphites: "Sulfites",
    lupin: "Lupin", molluscs: "Mollusques",
  },
  it: {
    gluten: "Glutine", crustaceans: "Crostacei", eggs: "Uova", fish: "Pesce", peanuts: "Arachidi", soybeans: "Soia",
    milk: "Latte", nuts: "Frutta a guscio", celery: "Sedano", mustard: "Senape", sesame: "Sesamo", sulphites: "Solfiti",
    lupin: "Lupini", molluscs: "Molluschi",
  },
  es: {
    gluten: "Gluten", crustaceans: "Crustáceos", eggs: "Huevos", fish: "Pescado", peanuts: "Cacahuetes", soybeans: "Soja",
    milk: "Leche", nuts: "Frutos de cáscara", celery: "Apio", mustard: "Mostaza", sesame: "Sésamo", sulphites: "Sulfitos",
    lupin: "Altramuces", molluscs: "Moluscos",
  },
};
ALLERGEN_LABELS.hr = { ...ALLERGEN_LABELS.bs!, milk: "Mlijeko" };
ALLERGEN_LABELS["sr-Latn"] = { ...ALLERGEN_LABELS.bs!, milk: "Mleko" };

export function allergenLabel(lang: string, allergen: Allergen): string {
  const dict = ALLERGEN_LABELS[lang] ?? ALLERGEN_LABELS[lang.split("-")[0]!] ?? ALLERGEN_LABELS.en!;
  return dict[allergen];
}

/** Short numeric codes commonly printed on EU menus (1–14). */
export const ALLERGEN_NUMBER: Record<Allergen, number> = {
  gluten: 1, crustaceans: 2, eggs: 3, fish: 4, peanuts: 5, soybeans: 6, milk: 7, nuts: 8, celery: 9, mustard: 10,
  sesame: 11, sulphites: 12, lupin: 13, molluscs: 14,
};

const DIETARY_LABELS: Record<string, Record<DietaryTag, string>> = {
  en: { vegetarian: "Vegetarian", vegan: "Vegan", "gluten-free": "Gluten-free", halal: "Halal", kosher: "Kosher", spicy: "Spicy", new: "New", signature: "Signature" },
  de: { vegetarian: "Vegetarisch", vegan: "Vegan", "gluten-free": "Glutenfrei", halal: "Halal", kosher: "Koscher", spicy: "Scharf", new: "Neu", signature: "Spezialität" },
  bs: { vegetarian: "Vegetarijansko", vegan: "Vegansko", "gluten-free": "Bez glutena", halal: "Halal", kosher: "Košer", spicy: "Ljuto", new: "Novo", signature: "Specijalitet" },
};

export function dietaryLabel(lang: string, tag: DietaryTag): string {
  const dict = DIETARY_LABELS[lang] ?? DIETARY_LABELS[lang.split("-")[0]!] ?? DIETARY_LABELS.en!;
  return dict[tag];
}
